import { type ReactNode } from "react"
import { Hourglass, MoreHorizontal } from "lucide-react"

/**
 * App 下载落地页（SPRINT_278 T-7525；SPRINT_520 T-9325 简化 + 渠道判定修正）。
 *
 * 分发事实（勿改文案前提，2026-10-11 SPRINT_758 T-11776 修订）：
 * - 对外分发口径 = 应用市场（iOS 去 App Store、Android 去应用宝），
 *   链接单源在 `src/data/appMarketLinks.ts`（当前为市场地址，上架后换详情页）；
 * - 页面不放置内测 OTA / staging 链接（河 2026-10-11：页面不要有 staging 相关地址）；
 *   内测设备安装入口（staging.zyhtb.cn/ota/，SPRINT_754 单一 OTA 基址）只供内部测试使用，不进本页。
 *
 * T-9325（⑫ 河拍板「一句话 + 一个图标」）：webview 里的下载引导收敛为「一个图标 + 一句话」。
 * 渠道判定补「小程序 web-view」一档：它同样是微信 webview（UA 含 MicroMessenger），
 * 小程序 web-view 的胶囊菜单官方文档未承诺「在浏览器打开」，故该档只讲官方保证的事。
 *
 * ⏳ 上架审核中口径（SPRINT_817 T-12301，2026-10-15 河拍板）：
 * App 双端已提审未上架——本页不承诺「可下载」，iOS/Android 市场按钮收掉
 * （市场首页搜不到本 App，是死路），各渠道如实说明 + 引导先用微信小程序。
 *
 * **上架后回填点（一次性手工换回，无开关无灰度）**：
 * 1. 恢复 iOS / Android 市场按钮（样式见 git 历史本文件 2026-10-14 版：`<a href={APP_MARKET_LINKS.ios}>去 App Store 下载</a>` 等）+ 恢复 `APP_MARKET_LINKS` import；
 * 2. 标题换回「下载智因错题本 App」、副标题去掉「上架审核中」句；
 * 3. wechat 渠道换回「点右上角「···」选「在浏览器打开」就能装」指引；
 * 4. `appMarketLinks.ts` 两常量换应用详情页链接（见该文件头注）；
 * 5. weapp 六露出 / 报告海报文案同批换回「可下载」口径（SPRINT_817 T-12302 改的对称面）。
 */

type Channel = "miniprogram" | "wechat" | "ios" | "android" | "desktop"

declare global {
  interface Window {
    /** 微信客户端在 web-view 内注入；小程序 web-view 下为 "miniprogram" */
    __wxjs_environment?: string
  }
}

/**
 * 渠道判定。`wxjsEnvironment` 传 `window.__wxjs_environment`（可缺省）。
 *
 * 小程序 web-view 必须排在 MicroMessenger 之前：它的 UA 同时含
 * MicroMessenger，排在后面就永远命中不到。
 * 判据取微信官方 web-view 文档给的两个信号（本页不引 JSSDK，故不用
 * `wx.miniProgram.getEnv`）：UA 含 `miniProgram`（微信 7.0.0+）、
 * 环境变量 `__wxjs_environment === "miniprogram"`。
 */
function detectChannel(ua: string, wxjsEnvironment?: string): Channel {
  if (wxjsEnvironment === "miniprogram" || /miniProgram/i.test(ua)) return "miniprogram"
  if (/MicroMessenger/i.test(ua)) return "wechat"
  if (/Android/i.test(ua)) return "android"
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && "ontouchend" in document)) {
    return "ios"
  }
  return "desktop"
}

/** 状态卡：一个图标 + 一句话（T-9325 ⑫ 口径的唯一落点；上架审核中期间的唯一形态） */
function GuideCard({
  icon,
  sentence,
  children,
}: {
  icon: ReactNode
  sentence: string
  children?: ReactNode
}) {
  return (
    <section className="paper-surface rounded-lg border border-hairline p-6 sm:p-8 text-center">
      <div className="flex justify-center text-primary" aria-hidden="true">
        {icon}
      </div>
      <p className="mt-4 text-heading-sm">{sentence}</p>
      {children}
    </section>
  )
}

export function DownloadPage() {
  const channel = detectChannel(navigator.userAgent, window.__wxjs_environment)

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="max-w-5xl mx-auto px-4 sm:px-8 h-16 flex items-center">
        <a href="/" className="flex items-center gap-2">
          <img
            src="/landing/logo.jpeg"
            alt="智因错题本"
            className="w-8 h-8 rounded-md object-cover"
          />
          <span className="text-label text-ink">智因错题本</span>
        </a>
      </header>

      <main className="max-w-md mx-auto px-4 pb-24 pt-8 sm:pt-16">
        <div className="text-center mb-10">
          <h1 className="text-heading mb-3">智因错题本 App 即将上线</h1>
          <p className="text-body text-ink-soft">
            错题拍照收录、AI 训练、复习提醒，学习数据与微信小程序实时同步。App 正在应用商店上架审核中，上线后即可在本页下载。
          </p>
        </div>

        {channel === "wechat" && (
          <GuideCard
            icon={<MoreHorizontal className="h-10 w-10" />}
            sentence="App 还在上架审核中；可以先在微信里使用小程序「智因错题本」"
          />
        )}

        {channel === "miniprogram" && (
          <GuideCard
            icon={<Hourglass className="h-10 w-10" />}
            sentence="App 还在上架审核中；小程序功能同样完整，可以先继续使用"
          />
        )}

        {channel === "ios" && (
          <GuideCard
            icon={<Hourglass className="h-10 w-10" />}
            sentence="App 还在上架审核中，上线后可在 App Store 搜索「智因错题本」下载"
          />
        )}

        {channel === "android" && (
          <GuideCard
            icon={<Hourglass className="h-10 w-10" />}
            sentence="App 还在上架审核中，上线后可在应用宝搜索「智因错题本」下载；也可以先用微信小程序"
          />
        )}

        {channel === "desktop" && (
          <GuideCard
            icon={<Hourglass className="h-10 w-10" />}
            sentence="App 正在上架审核中，上线后可用手机访问本页下载；也可以先使用微信小程序「智因错题本」"
          />
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-4 pb-10 text-center">
        <p className="text-caption text-ink-soft/70">
          <a href="/" className="hover:text-ink-soft">返回官网</a>
          <span className="mx-2">·</span>
          <a href="/privacy" className="hover:text-ink-soft">隐私政策</a>
        </p>
      </footer>
    </div>
  )
}
